import { useCallback, useEffect, useRef, useState } from 'react';
import { useSpeechRecognition } from 'react-speech-recognition';
import SpeechRecognition from 'react-speech-recognition';
import {
  clearActiveDictationStop,
  registerActiveDictationStop,
  stopActiveDictationSession,
} from './dictationSessionRegistry';

/**
 * Cloud speech polyfills (e.g. Azure) can be slow to release the mic on
 * abort. We keep the same race here — even though this app currently uses
 * the browser-native Web Speech API — so a future polyfill drop-in doesn't
 * strand a stopped session with the tab-level mic indicator still lit.
 * See https://github.com/JamesBrill/react-speech-recognition/issues/148
 */
const ABORT_RECOGNITION_TIMEOUT_MS = 3000;

async function raceAbortSpeechRecognition(): Promise<void> {
  await Promise.race([
    SpeechRecognition.abortListening(),
    new Promise<void>((resolve) => setTimeout(resolve, ABORT_RECOGNITION_TIMEOUT_MS)),
  ]);
}

/**
 * Wraps react-speech-recognition for a live-splice flow: the caller
 * snapshots the target field's state in `onStart`, watches `transcript`
 * grow via useEffect while `listening` is true and splices the interim
 * transcript into the field on every render, then either keeps that state
 * (Done → `onCommit`) or rolls back to the snapshot (Cancel → `onCancel`).
 *
 * Passing no handlers still works — you can just consume `transcript` and
 * `listening` and manage the flow yourself.
 */
export interface DictationHandlers {
  /** Fires right after the mic starts and the transcript is reset to ''. Use to snapshot the target field's pre-dictation state. */
  onStart?: () => void;
  /** Fires when the user clicks Done. The last transcript has already been surfaced via `transcript`; caller keeps whatever state it built up. */
  onCommit?: () => void;
  /** Fires when the user clicks Cancel. Caller should restore whatever they snapshotted in `onStart`. */
  onCancel?: () => void;
}

export function useDictation(handlers?: DictationHandlers) {
  const { transcript, listening, browserSupportsSpeechRecognition, resetTranscript } = useSpeechRecognition();
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isManuallyStopped, setIsManuallyStopped] = useState(false);
  // react-speech-recognition exposes `listening`/`transcript` as a *singleton*
  // — every useDictation() consumer in the tree subscribes to the same
  // engine, so a mic started by one field would light up the others too.
  // This flag tracks whether *this* hook instance is the one that started
  // the current session, so we can scope the returned state to it.
  const [isOwnSession, setIsOwnSession] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  // The exact callback we registered as the active-session stop. Held so
  // cancel/confirm can pass the same reference back to clearActiveDictationStop
  // (which compares by identity) and null the registry slot without
  // re-firing this callback.
  const registeredStopRef = useRef<(() => void) | null>(null);

  // Handlers can identity-change every render (inline objects); pin them
  // in a ref so start/cancel/confirm callbacks stay stable and never end
  // up capturing a stale snapshot.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);
  // Forward-ref to cancelListening — the registry callback is created
  // inside startListening (before cancelListening is defined in this
  // closure), so we route the call through a ref that we populate below.
  const cancelListeningRef = useRef<(() => void) | null>(null);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setAudioStream(null);
  }, []);

  const startListening = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) return;

    // The underlying speech engine is a singleton — a second startListening
    // on a sibling field would otherwise leave both hook instances with
    // `isOwnSession=true` splicing the same transcript. Tear down whichever
    // session is currently live (if any) before claiming the slot.
    stopActiveDictationSession();

    setIsManuallyStopped(false);
    try {
      // Duplicate getUserMedia stream keeps the tab mic indicator responsive
      // even when the underlying recognition tears down asynchronously.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setAudioStream(stream);

      resetTranscript();
      SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
      setIsOwnSession(true);
      // Register cancel as the active-session stop so a later
      // startListening from another instance — or a route change — can
      // tear us down. Keep the exact callback in a ref so cancel/confirm
      // can identity-clear the slot without re-firing it.
      const stopForRegistry = () => cancelListeningRef.current?.();
      registeredStopRef.current = stopForRegistry;
      registerActiveDictationStop(stopForRegistry);
      // Fire onStart AFTER the transcript reset so the caller's snapshot
      // isn't racing a leftover transcript from a previous session.
      handlersRef.current?.onStart?.();
    } catch (err) {
      console.error('Failed to start dictation:', err);
      cleanupStream();
    }
  }, [browserSupportsSpeechRecognition, resetTranscript, cleanupStream]);

  const cancelListening = useCallback(() => {
    setIsManuallyStopped(true);
    setIsOwnSession(false);
    // Drop the registry slot without re-firing this callback.
    if (registeredStopRef.current) {
      clearActiveDictationStop(registeredStopRef.current);
      registeredStopRef.current = null;
    }
    // Fire the caller's rollback synchronously — before any async recognition
    // teardown — so the field snaps back instantly and doesn't briefly show
    // stale spliced content while the library shuts down.
    handlersRef.current?.onCancel?.();
    // Release our duplicate stream first so the tab mic indicator drops even
    // if recognition tears down asynchronously.
    cleanupStream();
    void raceAbortSpeechRecognition()
      .catch(() => {
        /* recognition may already be idle */
      })
      .finally(() => {
        resetTranscript();
        setIsFinalizing(false);
      });
  }, [cleanupStream, resetTranscript]);

  useEffect(() => {
    cancelListeningRef.current = cancelListening;
  }, [cancelListening]);

  const confirmListening = useCallback(() => {
    setIsManuallyStopped(true);
    setIsOwnSession(false);
    setIsFinalizing(true);
    // Drop the registry slot without firing cancelListening.
    if (registeredStopRef.current) {
      clearActiveDictationStop(registeredStopRef.current);
      registeredStopRef.current = null;
    }

    handlersRef.current?.onCommit?.();

    cleanupStream();
    void raceAbortSpeechRecognition()
      .catch(() => {
        /* recognition may already be idle */
      })
      .finally(() => {
        resetTranscript();
      });

    setIsFinalizing(false);
  }, [cleanupStream, resetTranscript]);

  useEffect(() => {
    return () => {
      // If this instance owns the live session at unmount, tear it down
      // fully — otherwise a route change / component removal would leave
      // the registry pointing at a dead callback and the mic still lit.
      if (registeredStopRef.current) {
        cancelListeningRef.current?.();
      } else {
        cleanupStream();
      }
    };
  }, [cleanupStream]);

  // react-speech-recognition can lag flipping `listening` false on abort.
  // Track a manual-stop flag so the UI reflects the stop immediately after
  // cancel/confirm rather than waiting for the library's event to fire.
  // Also scope by `isOwnSession` so a mic started on a sibling useDictation
  // consumer doesn't light up this one — the underlying engine is shared.
  const effectiveListening = listening && !isManuallyStopped && isOwnSession;

  return {
    listening: effectiveListening,
    /** Live interim transcript — grows as the user speaks. Empty between sessions.
     *  Scoped to *this* instance so a sibling consumer's transcript doesn't leak in. */
    transcript: isOwnSession ? transcript : '',
    audioStream,
    isFinalizing,
    browserSupportsSpeechRecognition,
    startListening,
    cancelListening,
    confirmListening,
  };
}
